"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";

import { useCoupons, useOrders, usePlaceOrder, useProducts } from "../../../_lib/hooks";
import { useSession } from "../../../_lib/session";
import type { Product } from "../../../_lib/types";
import { DEFAULT_SETTINGS } from "../../../_lib/siteConfig";
import { useSite } from "../../../_lib/useSite";
import { Tooltip } from "../../../_ui/Tooltip";
import {
  ago,
  hidePhone,
  hyphenate,
  isMobile,
  phoneDigits,
  purchasable,
  qtyLimit,
  readDraftPhone,
  readHiddenPhones,
  writeDraftPhone,
} from "../shell/buy";
import { Zone } from "../blocks/Zone";
import { CardArt } from "../shell/CardArt";
import { Landing } from "../shell/Landing";
import { LoginGateSheet } from "../shell/LoginGateSheet";
import { Action, Card, Empty, IconChevron, IconClose, Note, PageTitle, Problem, Slot, Waiting } from "../shell/parts";
import { Column, Shell } from "../shell/Shell";

/*
 * 홈 = 구매 화면 — 시안 01(첫 방문) · 02(로그인 유도) · 04(재방문) · 14(PC) · 15(PC 랜딩).
 *
 * 🔴 **홈이 곧 구매 화면입니다**(README §3). 둘러보는 단계가 없고, 처음 온 사람도 상품권이 하나
 *    골라진 상태로 시작합니다. 본문은 세 덩어리 — ① 상품권 ② 받는 사람 ③ 구매 버튼.
 * 🔴 **재방문이 진짜 설계 지점입니다.** 지난 주문의 «받는 사람 번호» 를 즐겨찾기로 보여 주고
 *    (🔴 **가리지 않습니다** — 오너 결정: 가리면 어느 번호인지 고를 수 없습니다), 기본 선택도
 *    가장 최근에 산 상품권입니다.
 * 🔴 **`pcLanding` 이 켜지면 PC 폭(lg 이상)에서 랜딩(15)** 이고 모바일 폭은 그대로 구매 화면입니다.
 *    같은 주소라 **CSS 미디어쿼리로만** 가릅니다 — JS 로 폭을 재면 서버 HTML 과 첫 그림이 어긋납니다.
 * 🔴 **손님이 «구매하기» 를 누르면 시트(02)** 입니다 — 화면을 옮기지 않습니다. 고른 것은 `next`
 *    주소에, 적은 번호는 세션 저장소에 남아 로그인·가입 뒤 이 화면으로 돌아옵니다.
 * 🔴 결제 규칙(두 번 결제 방지 · `replace` 이동 · 발급 지연)은 `usePlaceOrder` 가 맡습니다.
 * 🆕 **문구와 블록 자리** (2026-09-28 · `docs/base/12`) — 제목·받는 사람 칸 이름·구매 버튼 문구는 관리자 설정
 *    (`settings.copy*`)입니다. 🔄 **비어 있으면 그 자리를 뺍니다** — 제목은 화면에서 빠지고(낭독기용 제목은 남김),
 *    받는 사람 칸 이름도 화면에서만 빠지며(입력칸 이름은 낭독기에 남김), 버튼은 문구만(금액 없음 · 비면 기본 문구). 그리고 구매 카드 위·아래에 블록 자리(`home.top` · `home.bottom`)가 있습니다.
 *    🔴 **구매 카드 자체는 잠긴 곳**입니다 — 자리는 카드 밖에만 있습니다.
 */
export default function Home() {
  const site = useSite();
  const pcLanding = site.skin === "simple" && site.settings.pcLanding;

  return (
    <Shell width="full">
      <div className={`flex flex-1 flex-col ${pcLanding ? "lg:hidden" : ""}`}>
        <Column>
          <Suspense fallback={<Waiting />}>
            <BuyForm />
          </Suspense>
        </Column>
      </div>
      {pcLanding && (
        <div className="hidden lg:block">
          <Landing />
        </div>
      )}
    </Shell>
  );
}

function BuyForm() {
  const site = useSite();
  const copy = site.skin === "simple" ? site.settings : DEFAULT_SETTINGS.simple;
  /* 🔄 제목을 비우면 화면에서 빠집니다 — 다만 화면에 제목(h1)이 하나도 없으면 낭독기 사용자가 어디인지 모르므로 서비스명을 숨겨 둡니다. */
  const title = copy.copyHomeTitle ? <PageTitle>{copy.copyHomeTitle}</PageTitle> : <h1 className="sr-only">{site.brand.siteName}</h1>;
  const recipientLabel = copy.copyRecipientLabel || <span className="sr-only">받는 사람</span>;
  /* 🔄 버튼에는 문구만 — 금액은 바로 위 «결제 금액» 줄에 있습니다(오너 지시). 버튼은 빌 수 없어 비면 기본 문구. */
  const buyLabel = copy.copyBuySuffix || DEFAULT_SETTINGS.simple.copyBuySuffix;
  const params = useSearchParams();
  const session = useSession();
  const member = session.status === "member";
  const products = useProducts();
  /* 🔑 재방문 — 지난 주문(번호 즐겨찾기)과 최근에 산 상품권(기본 선택). 둘 다 회원일 때만 부릅니다. */
  const orders = useOrders(1);
  const bought = useCoupons("", 1, { perPage: 20, enabled: member });
  const order = usePlaceOrder();

  const list = products.data ?? [];
  const buyable = list.filter(purchasable);
  const wantedId = Number(params.get("product") ?? "");
  const fromUrl = buyable.find((p) => p.id === wantedId) ?? null;
  /* 쿠폰에는 상품 id 가 없어 이름으로 짝을 맞춥니다(목록의 이름은 같은 상품 거울에서 옵니다). */
  const lastName = bought.data?.items[0]?.productName;
  const lastBought = lastName ? (buyable.find((p) => p.name === lastName) ?? null) : null;
  const waitingForHistory = !fromUrl && member && bought.loading;
  const selected: Product | null = fromUrl ?? lastBought ?? buyable[0] ?? null;

  /* 수량 — 상품이 바뀌면 1 로. 주소의 `qty` 는 그 상품일 때만 받습니다(로그인 뒤 돌아오기). */
  const [qtyFor, setQtyFor] = useState<{ id: number; n: number }>(() => ({
    id: wantedId,
    n: Math.max(1, Math.floor(Number(params.get("qty") ?? "1")) || 1),
  }));
  const limit = qtyLimit(selected);
  const qty = selected && qtyFor.id === selected.id ? Math.min(limit, qtyFor.n) : 1;
  const setQty = (n: number) => selected && setQtyFor({ id: selected.id, n: Math.max(1, Math.min(limit, n)) });

  /* 받는 사람 — 적는 동안 세션 저장소에 남깁니다(고르기 화면·로그인에 다녀와도 그대로). */
  const [phone, setPhoneState] = useState("");
  const [phoneError, setPhoneError] = useState("");
  useEffect(() => setPhoneState(readDraftPhone()), []);
  function setPhone(value: string) {
    const next = hyphenate(value);
    setPhoneState(next);
    setPhoneError("");
    writeDraftPhone(next);
  }

  const [hidden, setHidden] = useState<string[]>([]);
  useEffect(() => setHidden(readHiddenPhones()), []);
  const recents = useMemo(() => {
    const seen = new Map<string, string>();
    for (const item of orders.data?.items ?? []) {
      const d = phoneDigits(item.recipientPhone ?? "");
      if (!isMobile(d) || seen.has(d) || hidden.includes(d)) continue;
      seen.set(d, item.createdAt);
    }
    return [...seen].slice(0, 5);
  }, [orders.data, hidden]);

  const [gate, setGate] = useState(false);
  const total = selected ? selected.salePrice * qty : 0;

  async function buy() {
    if (!selected || order.busy) return;
    const digits = phoneDigits(phone);
    if (digits && !isMobile(digits)) {
      setPhoneError("휴대폰 번호를 확인해 주세요.");
      return;
    }
    if (session.status === "loading") return;
    if (!member) {
      setGate(true);
      return;
    }
    writeDraftPhone("");
    await order.place([{ productId: selected.id, qty }], { recipientPhone: digits || undefined });
  }

  if (products.loading && !products.data) return <>{title}<Card><Waiting /></Card></>;

  if (products.error) {
    return (
      <>
        {title}
        <Card>
          <Problem message={products.error} action={<Action size="sm" variant="secondary" tooltip="상품권 목록을 다시 불러옵니다." onClick={products.reload}>다시 시도</Action>} />
        </Card>
      </>
    );
  }

  /* 🔴 발급 지연(9002) — 결제는 끝났습니다. 구매 버튼을 없애 두 번 결제를 막습니다(`usePlaceOrder` 머리말). */
  if (order.delayed) {
    return (
      <>
        <PageTitle>결제가 접수됐습니다</PageTitle>
        <Card>
          <Problem message={order.delayed} />
          <div className="mt-4">
            <Action href="/orders" replace tooltip="주문 내역에서 발급 상태를 확인합니다.">주문 내역 보기</Action>
          </div>
        </Card>
      </>
    );
  }

  return (
    <>
      {title}
      <Zone name="home.top" className="mb-3" />
      <Card>
        {!selected && !waitingForHistory ? (
          <Empty>지금 살 수 있는 상품권이 없습니다.</Empty>
        ) : (
          <>
            <Slot label="상품권">
              {waitingForHistory || !selected ? (
                <div className="mt-2.5 h-12" aria-hidden="true" />
              ) : (
                <>
                  <Tooltip text="다른 상품권을 고르는 화면으로 갑니다." className="mt-2.5 flex w-full">
                    {(trigger) => (
                      <Link href={`/products?selected=${selected.id}`} {...trigger} className="flex w-full items-center gap-[13px] text-ink">
                        <CardArt name={selected.name} brand={selected.brandName} width={48} className="lg:hidden" />
                        <CardArt name={selected.name} brand={selected.brandName} width={50} className="hidden lg:block" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-subtitle font-bold tracking-[-0.01em] lg:text-[length:calc(var(--tnf-text-base)*18/14)]">{selected.name}</span>
                          <span className="mt-0.5 block text-body text-ink-soft tabular-nums lg:text-[length:calc(var(--tnf-text-base)*14.5/14)]">{selected.salePrice.toLocaleString("ko-KR")}원</span>
                        </span>
                        <IconChevron className="text-ink-faint" />
                        <span className="sr-only">상품권 바꾸기</span>
                      </Link>
                    )}
                  </Tooltip>
                  {/* README §11-9 — 수량은 상품권이 골라진 뒤에만 나타납니다. */}
                  <div className="mt-3.5 flex items-center justify-between gap-2.5 border-t border-line pt-3.5">
                    <span className="text-body font-medium text-ink-soft lg:text-[length:calc(var(--tnf-text-base)*14.5/14)]">수량</span>
                    <span className="inline-flex items-center gap-0.5 rounded-[var(--tnf-radius-sm)] border border-line bg-surface p-[3px]">
                      <StepButton label="수량 줄이기" tooltip="한 장 줄입니다." disabled={qty <= 1} onClick={() => setQty(qty - 1)}>−</StepButton>
                      <span aria-live="polite" className="w-10 text-center text-subtitle font-bold tabular-nums lg:w-[42px]">{qty}</span>
                      <StepButton label="수량 늘리기" tooltip={qty >= limit ? `한 번에 ${limit}장까지 살 수 있습니다.` : "한 장 늘립니다."} disabled={qty >= limit} onClick={() => setQty(qty + 1)}>+</StepButton>
                    </span>
                  </div>
                </>
              )}
            </Slot>

            <div className="h-2.5" />

            <Slot label={recipientLabel} labelFor="simple-recipient">
              <RecipientInput value={phone} onChange={setPhone} recents={recents} error={phoneError} onHide={(d) => { hidePhone(d); setHidden(readHiddenPhones()); }} />
              {phoneError ? (
                <p role="alert" className="mx-0.5 mt-2 text-caption font-bold text-danger">{phoneError}</p>
              ) : (
                <p className="mx-0.5 mt-2 text-caption text-ink-faint">비우면 내 번호로 발급됩니다.</p>
              )}
            </Slot>

            <div className="flex items-baseline justify-between px-1 pt-[18px]">
              <span className="text-lead font-medium text-ink-soft">결제 금액</span>
              <span className="text-hero font-bold tracking-[-0.03em] text-ink tabular-nums">
                {total.toLocaleString("ko-KR")}
                <span className="ml-0.5 text-[length:calc(var(--tnf-text-base)*16/14)]/[1.5] font-medium text-ink-soft">원</span>
              </span>
            </div>

            {order.error && <div className="mt-3"><Problem message={order.error} /></div>}

            <div className="mt-4">
              {order.payNext ? (
                /* 🔑 실 PG 가 붙은 뒤의 자리 — 지금(`fake`)은 도달하지 않습니다. */
                <a href={order.payNext.url} target="_blank" rel="noopener noreferrer" className="flex h-[58px] w-full items-center justify-center rounded-[var(--tnf-radius-md)] bg-accent text-subtitle font-bold text-on-accent">
                  결제 계속하기<span className="sr-only"> (새 창)</span>
                </a>
              ) : (
                <Action
                  size="xl"
                  disabled={!selected || waitingForHistory}
                  loading={order.busy}
                  loadingText="결제하고 발급하는 중입니다"
                  tooltip={member ? "결제하고 바로 발급합니다. 핀번호는 내 상품권에 저장됩니다." : "구매하려면 로그인이 필요합니다."}
                  onClick={buy}
                >
                  {buyLabel}
                </Action>
              )}
            </div>
          </>
        )}
      </Card>
      <Note>미사용 상품권은 결제 수단으로 환불됩니다.</Note>
      <Zone name="home.bottom" className="mt-4" />
      {selected && <LoginGateSheet open={gate} onClose={() => setGate(false)} next={`/?product=${selected.id}&qty=${qty}`} />}
    </>
  );
}

function StepButton({ label, tooltip, disabled, onClick, children }: { label: string; tooltip: string; disabled: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <Tooltip text={tooltip} align="right">
      {(trigger) => (
        <button
          type="button"
          aria-label={label}
          disabled={disabled}
          onClick={onClick}
          className="h-[38px] w-10 rounded-[var(--tnf-radius-xs)] text-[length:calc(var(--tnf-text-base)*20/14)] text-ink-soft hover:bg-raised disabled:opacity-35 lg:w-[42px]"
          {...trigger}
        >
          {children}
        </button>
      )}
    </Tooltip>
  );
}

/**
 * 받는 사람 칸 + 최근 보낸 번호 (시안 04). 🔴 번호를 가리지 않습니다(오너 결정).
 * 🔑 «지우기» 는 이 기기의 목록에서만 뺍니다 — 주문 기록은 그대로입니다(`buy.ts`).
 */
function RecipientInput({ value, onChange, recents, error, onHide }: {
  value: string;
  onChange: (value: string) => void;
  recents: [string, string][];
  error: string;
  onHide: (digits: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const hasRecents = recents.length > 0;

  useEffect(() => {
    if (!open) return;
    function onDown(event: PointerEvent) {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!hasRecents) setOpen(false);
  }, [hasRecents]);

  return (
    <div ref={box} className="relative mt-2">
      <input
        id="simple-recipient"
        type="tel"
        inputMode="numeric"
        autoComplete="tel"
        placeholder="010-0000-0000"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={error ? true : undefined}
        className={`simple-input h-[54px] w-full rounded-[var(--tnf-radius-sm)] border bg-surface pl-3.5 text-[length:max(16px,calc(var(--tnf-text-base)*19/14))] font-medium tracking-[0.01em] text-ink tabular-nums outline-none placeholder:text-ink-faint/60 ${hasRecents ? "pr-[52px]" : "pr-3.5"} ${error ? "border-danger" : open ? "border-accent" : "border-line"}`}
      />
      {hasRecents && (
        <div className="absolute top-[5px] right-[5px]">
        <Tooltip text="최근에 보낸 번호에서 고릅니다." align="right">
          {(trigger) => (
            <button
              type="button"
              aria-label="최근 보낸 번호"
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className={`grid size-11 place-items-center rounded-[var(--tnf-radius-xs)] ${open ? "bg-accent-wash text-accent" : "text-ink-faint hover:bg-raised"}`}
              {...trigger}
            >
              <IconChevron dir={open ? "up" : "down"} />
            </button>
          )}
        </Tooltip>
        </div>
      )}
      {open && (
        <div className="absolute inset-x-0 top-[60px] z-20 rounded-[var(--tnf-radius-md)] border border-line bg-surface p-1.5 shadow-[0_12px_32px_-12px_rgb(25_31_40_/_28%)]">
          <div className="px-2.5 pt-2 pb-1.5 text-[length:calc(var(--tnf-text-base)*12.5/14)] font-medium text-ink-faint">최근 보낸 번호</div>
          <ul>
            {recents.map(([digits, when]) => (
              <li key={digits} className="flex items-center gap-2 px-1">
                <button
                  type="button"
                  onClick={() => {
                    onChange(digits);
                    setOpen(false);
                  }}
                  title="이 번호로 보냅니다."
                  className="flex flex-1 items-baseline gap-[9px] rounded-[var(--tnf-radius-xs)] px-2 py-[11px] text-left hover:bg-raised"
                >
                  <span className="text-subtitle font-medium text-ink tabular-nums">{hyphenate(digits)}</span>
                  <span className="text-[length:calc(var(--tnf-text-base)*12.5/14)] text-ink-faint">{ago(when)}</span>
                </button>
                <button
                  type="button"
                  aria-label={`${hyphenate(digits)} 지우기`}
                  title="이 기기의 목록에서 뺍니다."
                  onClick={() => onHide(digits)}
                  className="grid size-[34px] place-items-center rounded-[var(--tnf-radius-xs)] text-ink-faint hover:bg-raised hover:text-ink"
                >
                  <IconClose size={14} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
